import React from 'react';
import type {SetRequired} from 'type-fest';
import {type FieldValues, type FieldPath} from 'react-hook-form';
import {splitEventHandler} from '@netx/core-next/utils';
import {useConstCallback} from '@netx/core-components/hooks/useEvent';
import {combineRefs} from '@netx/core-components/util';
import {
	type NumberFieldProps,
	NumberField,
} from '@netx/core-components/NumberField';
import {
	ReactHookFormControl,
	type WithReactHookFormControlFieldProps,
} from '../ReactHookFormControl';

export interface ReactHookFormNumberFieldProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends WithReactHookFormControlFieldProps<
		SetRequired<NumberFieldProps, 'name'>,
		TFieldValues,
		TName
	> {}

export const ReactHookFormNumberFieldPrimitive = <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	{
		control,
		ControllerProps,
		...props
	}: ReactHookFormNumberFieldProps<TFieldValues, TName>,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	return (
		<ReactHookFormControl<TFieldValues, TName>
			{...ControllerProps}
			control={control}
			name={props.name as TName}
			render={({field, formState}) => (
				<NumberField
					error={formState.errors[props.name]?.message?.toString()}
					{...props}
					{...field}
					disabled={props.disabled || field.disabled}
					ref={ref}
					inputRef={combineRefs(props.inputRef, field.ref)}
					onChange={useConstCallback(
						splitEventHandler(props.onChange, (event, value) =>
							field.onChange(value),
						),
					)}
				/>
			)}
			rules={{
				required: props.required,
				...ControllerProps?.rules,
			}}
		/>
	);
};

/**
 * `react-hook-form` controlled `NumberField` component
 */
export const ReactHookFormNumberField = React.forwardRef(
	ReactHookFormNumberFieldPrimitive,
) as <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	props: ReactHookFormNumberFieldProps<TFieldValues, TName>,
) => JSX.Element;

export default ReactHookFormNumberField;
